// Canonical A2P stage model for the frontend.
//
// MIRRORS the server logic in src/api_routes/admin-a2p.js (isApproved/isFailed +
// the stage computation that powers the /admin/a2p "Tenant A2P Status" table).
// Keep the two in sync by hand if the stage rules ever change — the Get-Live
// banner and the admin dashboard must always agree about a tenant's stage.
//
// Stage progression (profile → brand → campaign):
//   not_started → profile_pending/approved/failed → brand_pending/approved/failed
//   → campaign_pending/failed → complete

export function isA2PApproved(status) {
  if (!status) return false;
  const s = status.toLowerCase();
  return s === 'approved' || s === 'twilio-approved' || s === 'verified' || s === 'active';
}

export function isA2PFailed(status) {
  if (!status) return false;
  const s = status.toLowerCase();
  return s === 'failed' || s === 'rejected' || s === 'suspended';
}

// Compute the overall A2P stage from the three records. Mirrors admin-a2p.js
// exactly: campaign state wins, then brand, then profile, else not_started.
export function computeA2PStage({ profile = null, brand = null, campaigns = [] } = {}) {
  if (campaigns.length > 0) {
    const anyApproved = campaigns.some(c => isA2PApproved(c.status));
    const allFailed = campaigns.every(c => isA2PFailed(c.status));
    return anyApproved ? 'complete' : allFailed ? 'campaign_failed' : 'campaign_pending';
  }
  if (brand) {
    return isA2PApproved(brand.status) ? 'brand_approved'
      : isA2PFailed(brand.status) ? 'brand_failed'
      : 'brand_pending';
  }
  if (profile) {
    return isA2PApproved(profile.status) ? 'profile_approved'
      : isA2PFailed(profile.status) ? 'profile_failed'
      : 'profile_pending';
  }
  return 'not_started';
}

// Banner copy + tone per stage. `tone` drives styling in the component:
//   'pending' = on track, no action (green) · 'action' = user must act (amber)
//   'failed'  = rejected, must fix (red) · null = nothing to show (complete).
export function a2pBannerContent(stage) {
  switch (stage) {
    case 'complete':
      return null;
    case 'not_started':
      return { tone: 'action', title: 'Finish your A2P setup to go live',
        subtitle: "You're texting on SurFox's shared number until your own campaign is approved." };
    case 'profile_pending':
      return { tone: 'pending', title: 'Your business profile is under review',
        subtitle: "We'll register your brand and campaign automatically once it clears." };
    case 'profile_approved':
      return { tone: 'pending', title: 'Business profile approved — registering your brand next',
        subtitle: 'No action needed; this continues automatically.' };
    case 'profile_failed':
      return { tone: 'failed', title: 'Your business profile needs attention',
        subtitle: 'Open A2P settings to review the reason and resubmit.' };
    case 'brand_pending':
      return { tone: 'pending', title: 'Your brand registration is under carrier review',
        subtitle: "You'll go live on your own number automatically once approved." };
    case 'brand_approved':
      return { tone: 'action', title: 'Finish your A2P setup — create your campaign to go live',
        subtitle: "You're texting on SurFox's shared number until your campaign is approved." };
    case 'brand_failed':
      return { tone: 'failed', title: 'Your brand registration was rejected',
        subtitle: 'Open A2P settings to review the reason and resubmit.' };
    case 'campaign_pending':
      return { tone: 'pending', title: 'Your campaign is pending carrier approval',
        subtitle: "You'll go live on your own number automatically once approved." };
    case 'campaign_failed':
      return { tone: 'failed', title: 'Your campaign was rejected',
        subtitle: 'Open A2P settings to review the reason and resubmit.' };
    default:
      return null;
  }
}
